import {PaymentMode as SapiPaymentMode} from '@findhotel/sapi'

import {PaymentMode as BookingPaymentMode} from '../../api-types/bovio/response/booking'
import {PaymentMode as OfferCheckPaymentMode} from '../../api-types/bovio/response/offer_check'

/**
 * Resolves how the guest pays from the backend-owned `paymentMode`, on every surface:
 * SAPI offers and rooms (SRP, AP), BoVio offer check (PP, CP) and booking (CP, BMP).
 */

/**
 * BoVio `paymentMode` from the offer check (what the offer is eligible for) or the
 * booking (what was consolidated at creation).
 */
export type BovioPaymentMode = OfferCheckPaymentMode | BookingPaymentMode

/**
 * `paymentMode` from SAPI or BoVio. SAPI also sends `unknown`.
 */
export type AcceptedPaymentMode = BovioPaymentMode | SapiPaymentMode

type PaymentModeSource = {
  paymentMode?: AcceptedPaymentMode | null
  canPayLater?: boolean | null
  package?: {canPayLater?: boolean | null} | null
}

export enum OfferPaymentMode {
  PayNow = 'payNow',
  PayAtProperty = 'payAtProperty',
  ChargeLater = 'chargeLater'
}

// Typed over every SAPI and BoVio value, so a new mode fails typecheck until it is mapped.
const PAYMENT_MODES: Record<
  `${AcceptedPaymentMode}`,
  OfferPaymentMode | undefined
> = {
  pay_now: OfferPaymentMode.PayNow,
  pay_at_property: OfferPaymentMode.PayAtProperty,
  charge_later: OfferPaymentMode.ChargeLater,
  unknown: undefined
}

const mapPaymentMode = (
  paymentMode?: AcceptedPaymentMode | null
): OfferPaymentMode | undefined =>
  paymentMode ? PAYMENT_MODES[paymentMode] : undefined

/**
 * Partners API offers have no `paymentMode`, only `package.canPayLater`, so it sets
 * their pay-at-property label. Also covers a missing or `unknown` SAPI mode.
 * @param offer - Offer without a known `paymentMode`.
 */
const getPartnerPaymentMode = (
  offer?: PaymentModeSource | null
): OfferPaymentMode =>
  offer?.canPayLater || offer?.package?.canPayLater
    ? OfferPaymentMode.PayAtProperty
    : OfferPaymentMode.PayNow

type GetOfferPaymentModeParams = {
  offer?: PaymentModeSource | null
  isSplitBooking?: boolean
  paymentMode?: AcceptedPaymentMode | null
}

/**
 * Single source of truth for the payment mode.
 * `paymentMode` wins over `offer.paymentMode`. A missing or `unknown` mode falls back to
 * the partner `canPayLater` flag. Split-booking offers are always `PayNow`.
 * @param params - The offer and/or its `paymentMode`, and whether it is a split booking.
 */
export const getOfferPaymentMode = (
  params: GetOfferPaymentModeParams
): OfferPaymentMode => {
  const {offer, isSplitBooking = false, paymentMode} = params
  if (isSplitBooking) return OfferPaymentMode.PayNow
  return (
    mapPaymentMode(paymentMode) ??
    mapPaymentMode(offer?.paymentMode) ??
    getPartnerPaymentMode(offer)
  )
}

/**
 * Pay-at-property check on every surface.
 * @param offer - Any offer, offer check or booking carrying `paymentMode`.
 */
export const hasPayAtPropertyPayment = (
  offer?: PaymentModeSource | null
): boolean => getOfferPaymentMode({offer}) === OfferPaymentMode.PayAtProperty

/**
 * "Book now, pay later" check on every surface. Split-booking offers are not eligible.
 * @param offer - Any offer, offer check or booking carrying `paymentMode`.
 * @param isSplitBooking - Determines if the offer is a split booking.
 */
export const hasChargeLaterPayment = (
  offer?: PaymentModeSource | null,
  isSplitBooking = false
): boolean =>
  getOfferPaymentMode({offer, isSplitBooking}) === OfferPaymentMode.ChargeLater
